import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../contexts/FilterContext';
import { API } from '../utils/api';
import { DataExport, PushNotifications, NotificationPreferences, WeeklyDigestPreferences } from '../components/shared';
import { UserAgreement, PrivacyStatement } from '../components/Legal';
import { EasterEggStats, EasterEggRewardHistory } from '../components/Gamification/FloatingEasterEggs';
import { CategoryManager } from '../components/Settings';
import PhotoUpload from '../components/Profile/PhotoUpload';
import UserControlPanelSection from '../components/settings/UserControlPanelSection';
import ContentFilterSection from '../components/settings/ContentFilterSection';
import InlineCategoryManager from '../components/settings/InlineCategoryManager';
import ContentDumpModal from '../components/shared/ContentDumpModal';
import useXHR from '../hooks/useXHR';

const SettingsPage = ({ showToast, setCurrentPage }) => {
  const { token, user, refreshUser } = useAuth();
  const { contentMinWords, setContentMinWords, certMinWords, setCertMinWords, exportFilters, loadPreferences } = useFilters();
  const { get, post, put } = useXHR(token);
  const [shareCode, setShareCode] = useState('');
  const [importCode, setImportCode] = useState('');
  const [settings, setSettings] = useState({
    ultimate_search_public: user?.ultimate_search_public || false,
    friends_visible: user?.friends_visible || false,
    content_filter: user?.content_filter || 'strict',
    ultimate_search_title: user?.ultimate_search_title || 'Ultimate Search Page',
    default_data_source: user?.default_data_source || 'worldwide',
    auto_refresh_map: user?.auto_refresh_map !== false,
    show_map_by_default: user?.show_map_by_default !== false,
    compact_sidebar: user?.compact_sidebar || false,
    dark_mode_intensity: user?.dark_mode_intensity || 'normal',
    notification_sounds: user?.notification_sounds !== false,
    urls_per_window: user?.urls_per_window || 600
  });
  
  const [hasPassword, setHasPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showEmailSection, setShowEmailSection] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [showLegal, setShowLegal] = useState(null);
  const [showContentDump, setShowContentDump] = useState(false);

  useEffect(() => {
    get('/users/has-password', {
      onSuccess: (data) => setHasPassword(data.has_password)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSettings = () => {
    put('/users/settings', {
      body: settings,
      onSuccess: () => { showToast('Settings saved!', 'success'); refreshUser(); },
      onError: (err) => showToast(err || 'Failed to save settings', 'error')
    });
  };

  const handleChangePassword = () => {
    if (newPassword.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    if (newPassword !== confirmPassword) { showToast('Passwords do not match', 'error'); return; }
    if (hasPassword && !currentPassword) { showToast('Current password is required', 'error'); return; }
    setPasswordLoading(true);
    post('/users/change-password', {
      body: { current_password: hasPassword ? currentPassword : null, new_password: newPassword },
      onSuccess: () => {
        showToast('Password changed successfully!', 'success');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
        setShowPasswordSection(false); setHasPassword(true);
      },
      onError: (err) => showToast(err || 'Failed to change password', 'error'),
      onFinally: () => setPasswordLoading(false)
    });
  };

  const handleChangeEmail = () => {
    if (!newEmail || !newEmail.includes('@')) { showToast('Please enter a valid email address', 'error'); return; }
    if (!emailPassword) { showToast('Password is required to change email', 'error'); return; }
    setEmailLoading(true);
    put('/auth/update-email', {
      body: { new_email: newEmail, password: emailPassword },
      onSuccess: () => {
        showToast('Email updated successfully!', 'success');
        setNewEmail(''); setEmailPassword(''); setShowEmailSection(false);
        if (refreshUser) refreshUser();
      },
      onError: (err) => showToast(err || 'Failed to change email', 'error'),
      onFinally: () => setEmailLoading(false)
    });
  };

  return (
    <div className="card" data-testid="settings-page">
      <div className="card-header"><h2>User Settings</h2></div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        
        {/* User Control Panel */}
        <UserControlPanelSection settings={settings} setSettings={setSettings} />

        {/* Privacy Toggles */}
        <div style={{ padding: 15, background: '#475569', borderRadius: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.ultimate_search_public}
              onChange={(e) => setSettings({ ...settings, ultimate_search_public: e.target.checked })} />
            <div>
              <strong>Make Ultimate Search Page Public</strong>
              <p style={{ fontSize: '0.8rem', color: '#a1a1aa', marginTop: 4 }}>Allow other users to view your Ultimate Search page and public categories</p>
            </div>
          </label>
        </div>

        <div style={{ padding: 15, background: '#475569', borderRadius: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.friends_visible}
              onChange={(e) => setSettings({ ...settings, friends_visible: e.target.checked })} />
            <div>
              <strong>Show Friends List to Others</strong>
              <p style={{ fontSize: '0.8rem', color: '#a1a1aa', marginTop: 4 }}>Allow other users to see your friends list</p>
            </div>
          </label>
        </div>

        {/* Content Filtering */}
        <ContentFilterSection settings={settings} setSettings={setSettings} />

        {/* URL Collection Window Settings */}
        <div style={{ padding: 15, background: '#475569', borderRadius: 10 }} data-testid="url-window-settings">
          <h4 style={{ margin: '0 0 10px', color: '#334155', fontSize: '0.95rem' }}>
            <i className="fas fa-window-restore" style={{ marginRight: 8, color: '#FDE047' }} />
            URL Collection Window Settings
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.8rem', color: '#a1a1aa' }}>Max URLs per Window:</label>
            <input
              type="range"
              min="50" max="10000" step="50"
              value={settings.urls_per_window}
              onChange={(e) => setSettings({ ...settings, urls_per_window: parseInt(e.target.value) })}
              style={{ flex: 1, maxWidth: 300, accentColor: '#FDE047' }}
              data-testid="urls-per-window-slider"
            />
            <input
              type="number"
              min="50" max="10000" step="50"
              value={settings.urls_per_window}
              onChange={(e) => setSettings({ ...settings, urls_per_window: Math.max(50, Math.min(10000, parseInt(e.target.value) || 400)) })}
              style={{ width: 70, background: 'rgba(253, 251, 247,0.8)', border: '1px solid rgba(253, 224, 71,0.3)', borderRadius: 6, color: '#334155', padding: '4px 8px', fontSize: '0.8rem', textAlign: 'center' }}
              data-testid="urls-per-window-input"
            />
            <span style={{ fontSize: '0.7rem', color: '#71717a' }}>Default: 400 | Max: 10,000</span>
          </div>
        </div>

        <button className="btn btn-primary" onClick={updateSettings} data-testid="save-settings-btn">Save Settings</button>

        {/* Account Info */}
        <div style={{ marginTop: 20, padding: 20, background: 'rgba(252, 211, 77, 0.1)', borderRadius: 10 }}>
          <h3 style={{ marginBottom: 15, color: '#047857' }}>Account Information</h3>
          <p><strong>Username:</strong> {user?.username}</p>
          <p><strong>Email:</strong> {user?.email}</p>
          <p><strong>Status:</strong> {user?.is_admin ? 'Admin' : (user?.is_paid ? 'Premium' : 'Free')}</p>
          
          {/* Email Change */}
          <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid rgba(252, 211, 77, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>Change Email Address</span>
              <button className="btn btn-secondary" onClick={() => setShowEmailSection(!showEmailSection)}
                style={{ padding: '6px 12px', fontSize: '0.85rem' }} data-testid="toggle-email-section-btn">
                {showEmailSection ? 'Cancel' : 'Change'}
              </button>
            </div>
            {showEmailSection && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                <input type="email" placeholder="New Email Address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="input" data-testid="new-email-input" />
                <input type="password" placeholder="Confirm with Password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className="input" data-testid="email-password-input" />
                <button className="btn btn-primary" onClick={handleChangeEmail} disabled={emailLoading} data-testid="change-email-btn">
                  {emailLoading ? 'Updating...' : 'Update Email'}
                </button>
                {!hasPassword && <p style={{ color: '#f59e0b', fontSize: '0.75rem', margin: 0 }}>You must set a password first before changing email (Google-only accounts)</p>}
              </div>
            )}
          </div>
        </div>

        <PhotoUpload showToast={showToast} onUpdate={() => refreshUser && refreshUser()} />

        {/* Inline Category Manager */}
        <InlineCategoryManager showToast={showToast} setCurrentPage={setCurrentPage} />

        {/* Password Change */}
        <div style={{ padding: 20, background: '#475569', borderRadius: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPasswordSection ? 15 : 0 }}>
            <h3 style={{ color: '#047857', margin: 0 }}>{hasPassword ? 'Change Password' : 'Set Password'}</h3>
            <button className="btn btn-secondary" onClick={() => setShowPasswordSection(!showPasswordSection)}
              style={{ padding: '8px 16px', fontSize: '0.9rem' }} data-testid="toggle-password-section-btn">
              {showPasswordSection ? 'Cancel' : hasPassword ? 'Change' : 'Set Password'}
            </button>
          </div>
          {!hasPassword && !showPasswordSection && (
            <p style={{ fontSize: '0.85rem', color: '#a1a1aa', marginTop: 10 }}>You signed in with Google. Set a password to also login with email.</p>
          )}
          {showPasswordSection && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {hasPassword && <input type="password" placeholder="Current Password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="input" data-testid="current-password-input" />}
              <input type="password" placeholder="New Password (min 6 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input" data-testid="new-password-input" />
              <input type="password" placeholder="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input" data-testid="confirm-password-input" />
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={passwordLoading} data-testid="change-password-btn">
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          )}
        </div>

        {/* Subscription */}
        {!user?.is_paid && !user?.is_admin && (
          <div style={{ padding: 20, background: 'linear-gradient(135deg, rgba(253, 230, 138, 0.2), rgba(252, 211, 77, 0.2))', borderRadius: 10, border: '1px solid rgba(253, 230, 138, 0.3)' }}>
            <h3 style={{ marginBottom: 10, color: '#047857' }}><i className="fas fa-heart" style={{ marginRight: 6 }} /> Support InfoPilot</h3>
            <p style={{ fontSize: '0.9rem', color: '#a1a1aa', marginBottom: 15 }}>All features are FREE! If you love InfoPilot, consider supporting us with an optional donation.</p>
            <button className="btn btn-primary" onClick={() => setCurrentPage('donation')} data-testid="support-btn">Support Us - Pay What You Want</button>
            <div style={{ marginTop: 15, padding: 12, background: 'rgba(16, 185, 129, 0.15)', borderRadius: 8, border: '1px dashed rgba(16, 185, 129, 0.4)' }} data-testid="support-message">
              <p style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 600, margin: '0 0 5px 0' }}>
                <i className="fas fa-gift" style={{ marginRight: 4 }} /> Everything is Free!
              </p>
              <p style={{ color: '#a1a1aa', fontSize: '0.75rem', margin: 0, lineHeight: 1.4 }}>
                InfoPilot Explorer is completely free to use. Your optional donations help cover server costs.
              </p>
            </div>
          </div>
        )}

        {/* Legal Documents */}
        <div style={{ marginTop: 20, padding: 20, background: 'rgba(39, 39, 42, 0.5)', borderRadius: 10 }}>
          <h3 style={{ marginBottom: 15, color: '#a1a1aa', fontSize: '1rem' }}><i className="fas fa-gavel" style={{ marginRight: 6 }} /> Legal Documents</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={() => setShowLegal('terms')} style={{ fontSize: '0.85rem' }} data-testid="view-terms-btn">
              <i className="fas fa-file-contract" style={{ marginRight: 4 }} /> User Agreement
            </button>
            <button className="btn btn-secondary" onClick={() => setShowLegal('privacy')} style={{ fontSize: '0.85rem' }} data-testid="view-privacy-btn">
              <i className="fas fa-lock" style={{ marginRight: 4 }} /> Privacy Policy
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-secondary" onClick={() => setCurrentPage('legal')} data-testid="view-full-legal-btn"
              style={{ fontSize: '0.85rem', background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.3), rgba(253, 230, 138, 0.3))' }}>
              <i className="fas fa-scroll" style={{ marginRight: 4 }} /> View Full Legal Documents Page
            </button>
          </div>
          <p style={{ color: '#71717a', fontSize: '0.75rem', marginTop: 10 }}>Top Pilot Enterprises, Inc. &bull; Brunswick, Maine</p>
        </div>
        {showLegal === 'terms' && <UserAgreement onClose={() => setShowLegal(null)} />}
        {showLegal === 'privacy' && <PrivacyStatement onClose={() => setShowLegal(null)} />}

        {/* Quick Links */}
        <div style={{ marginTop: 20, padding: 20, background: 'rgba(39, 39, 42, 0.5)', borderRadius: 10 }}>
          <h3 style={{ marginBottom: 15, color: '#a1a1aa', fontSize: '1rem' }}><i className="fas fa-rocket" style={{ marginRight: 6 }} /> Quick Links</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => setCurrentPage('personal-reports')} style={{ fontSize: '0.85rem' }} data-testid="go-to-personal-reports-btn">
              <i className="fas fa-file-alt" style={{ marginRight: 4 }} /> My Personal Reports
            </button>
            <button className="btn btn-primary" onClick={() => setCurrentPage('wallet')} data-testid="go-to-wallet-btn"
              style={{ fontSize: '0.85rem', background: 'linear-gradient(135deg, #0070ba, #003087)' }}>
              <i className="fas fa-wallet" style={{ marginRight: 4 }} /> PayPal Wallet & Payouts
            </button>
            <button className="btn btn-secondary" onClick={() => setCurrentPage('donation')} style={{ fontSize: '0.85rem' }} data-testid="go-to-support-btn">
              <i className="fas fa-heart" style={{ marginRight: 4 }} /> Support Us
            </button>
          </div>
        </div>

        {/* Category Management (Component) */}
        <div style={{ marginTop: 20, padding: 20, background: 'rgba(39, 39, 42, 0.5)', borderRadius: 10 }}>
          <h3 style={{ marginBottom: 15, color: '#047857', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-tags" /> Category Management
          </h3>
          <p style={{ color: '#a1a1aa', fontSize: '0.85rem', marginBottom: 15 }}>
            Manage your search categories, sub-categories, and protocols.
          </p>
          <CategoryManager showToast={showToast} />
        </div>

        {/* Easter Egg Stats & Settings */}
        <div style={{ marginTop: 20 }}><EasterEggStats /></div>
        <div style={{ marginTop: 20 }}><EasterEggRewardHistory /></div>

        <div style={{ marginTop: 20 }} data-testid="easter-egg-settings">
          <div className="ocean-shimmer" style={{ background: '#334155', borderRadius: 12, padding: 20, border: '1px solid rgba(253, 230, 138, 0.2)', position: 'relative' }}>
            <h3 style={{ color: '#047857', margin: '0 0 12px', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-egg" /> Floating Easter Eggs
            </h3>
            <p style={{ color: '#71717a', fontSize: '0.78rem', marginBottom: 12 }}>Control the frequency of floating easter egg surprises, or disable them entirely.</p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} data-testid="easter-egg-toggle">
                <div className="filter-pill" style={{
                  width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
                  background: (localStorage.getItem('easter_egg_enabled') !== 'false') ? 'linear-gradient(135deg, #FDE68A, #FDE047)' : 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(253, 230, 138,0.3)'
                }} onClick={() => {
                  const current = localStorage.getItem('easter_egg_enabled') !== 'false';
                  localStorage.setItem('easter_egg_enabled', (!current).toString());
                  showToast(current ? 'Easter eggs disabled' : 'Easter eggs enabled!', 'info');
                  window.location.reload();
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
                    left: (localStorage.getItem('easter_egg_enabled') !== 'false') ? 23 : 3, transition: 'left 0.2s'
                  }} />
                </div>
                <span style={{ color: '#334155', fontSize: '0.82rem' }}>
                  {localStorage.getItem('easter_egg_enabled') !== 'false' ? 'Enabled' : 'Disabled'}
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#a1a1aa', fontSize: '0.78rem' }}>Frequency:</span>
                <select value={localStorage.getItem('easter_egg_frequency') || '45000'}
                  onChange={(e) => { localStorage.setItem('easter_egg_frequency', e.target.value); showToast('Easter egg frequency updated! Reloading...', 'info'); setTimeout(() => window.location.reload(), 500); }}
                  data-testid="easter-egg-frequency"
                  style={{ background: 'rgba(253, 230, 138,0.1)', border: '1px solid rgba(253, 230, 138,0.3)', color: '#047857', padding: '6px 10px', borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <option value="15000">Very Frequent (15s)</option>
                  <option value="30000">Frequent (30s)</option>
                  <option value="45000">Normal (45s)</option>
                  <option value="90000">Rare (90s)</option>
                  <option value="180000">Very Rare (3m)</option>
                  <option value="300000">Ultra Rare (5m)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* AI Filter Suggestions */}
        <div style={{ marginTop: 20 }} data-testid="ai-filter-suggestion-section">
          <div style={{ background: '#334155', borderRadius: 12, padding: 20, border: '1px solid rgba(245, 158, 11, 0.25)' }}>
            <h3 style={{ color: '#f59e0b', margin: '0 0 12px', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-robot" /> AI Filter Suggestions
            </h3>
            <p style={{ color: '#71717a', fontSize: '0.78rem', marginBottom: 14 }}>Let AI analyze your search history and suggest optimal filter configurations.</p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" placeholder="Topic of interest (e.g. science, history...)" data-testid="ai-suggestion-topic-input" id="ai-topic-input"
                style={{ flex: 1, minWidth: 180, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', color: '#334155', padding: '8px 12px', borderRadius: 8, fontSize: '0.82rem' }} />
              <button data-testid="ai-suggestion-btn" onClick={() => {
                const topic = document.getElementById('ai-topic-input')?.value || 'general research';
                const btn = document.querySelector('[data-testid="ai-suggestion-btn"]');
                if (btn) { btn.disabled = true; btn.textContent = 'Analyzing...'; }
                const x = new XMLHttpRequest();
                x.open('POST', `${API}/filter-suggest`, true);
                x.setRequestHeader('Content-Type', 'application/json');
                x.setRequestHeader('Authorization', `Bearer ${token}`);
                x.onload = () => {
                  if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fas fa-magic"></i> Get AI Suggestion'; }
                  if (x.status < 300) {
                    try { const res = JSON.parse(x.responseText); showToast(`${res.suggestion?.explanation || 'Suggestion ready!'}\n\nTip: ${res.suggestion?.tip || ''}`, 'success'); }
                    catch(e) { showToast('Got suggestion but failed to parse', 'warning'); }
                  } else showToast('Failed to get AI suggestion', 'error');
                };
                x.onerror = () => { if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fas fa-magic"></i> Get AI Suggestion'; } showToast('Network error', 'error'); };
                x.send(JSON.stringify({ topic }));
              }}
                style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(253, 230, 138,0.1))', border: '1px solid rgba(245,158,11,0.35)', color: '#f59e0b', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                <i className="fas fa-magic" /> Get AI Suggestion
              </button>
            </div>
          </div>
        </div>

        {/* Filter Word Count Settings */}
        <div style={{ marginTop: 20 }} data-testid="filter-word-count-settings">
          <div style={{ background: '#334155', borderRadius: 12, padding: 20, border: '1px solid rgba(252, 211, 77, 0.25)' }}>
            <h3 style={{ color: '#047857', margin: '0 0 16px', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-sliders-h" /> Filter Word Count Settings
            </h3>
            <p style={{ color: '#71717a', fontSize: '0.78rem', marginBottom: 16 }}>Set minimum word counts for Content Preference and Certification filters.</p>
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ color: '#10b981', fontSize: '0.85rem', margin: '0 0 10px' }}>Content Preference Filters</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {Object.entries(contentMinWords).map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(16, 185, 129, 0.08)', borderRadius: 8, padding: '6px 10px', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                    <span style={{ color: '#334155', fontSize: '0.75rem', flex: 1, textTransform: 'capitalize' }}>{key}</span>
                    <input type="number" value={val} min={0} max={50000} step={100} data-testid={`content-min-words-${key}`}
                      onChange={e => setContentMinWords(prev => ({ ...prev, [key]: Math.max(0, parseInt(e.target.value) || 0) }))}
                      style={{ width: 60, background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981', padding: '3px 6px', borderRadius: 6, fontSize: '0.72rem', textAlign: 'center' }} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 10px' }}>Certification Filters</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {[{ key: 'pearsonvue', label: 'PearsonVUE Certified' }, { key: 'government', label: 'Government Source' }, { key: 'advancedDegree', label: 'Advanced Degree (PhD/Masters)' }].map(f => (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, padding: '6px 10px', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                    <span style={{ color: '#334155', fontSize: '0.75rem', flex: 1 }}>{f.label}</span>
                    <input type="number" value={certMinWords[f.key] || 0} min={0} max={50000} step={100} data-testid={`cert-min-words-${f.key}`}
                      onChange={e => setCertMinWords(prev => ({ ...prev, [f.key]: Math.max(0, parseInt(e.target.value) || 0) }))}
                      style={{ width: 60, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', padding: '3px 6px', borderRadius: 6, fontSize: '0.72rem', textAlign: 'center' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Persistence & Sharing */}
        <div style={{ marginTop: 20 }} data-testid="filter-persistence-section">
          <div className="ocean-shimmer" style={{ background: '#334155', borderRadius: 12, padding: 20, border: '1px solid rgba(253, 230, 138, 0.2)', position: 'relative' }}>
            <h3 style={{ color: '#38bdf8', margin: '0 0 16px', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-cloud-upload-alt" /> Filter Persistence & Sharing
            </h3>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <button onClick={() => {
                const filters = exportFilters();
                const x = new XMLHttpRequest();
                x.open('POST', `${API}/filter-preferences`, true);
                x.setRequestHeader('Content-Type', 'application/json');
                x.setRequestHeader('Authorization', `Bearer ${token}`);
                x.onload = () => { if (x.status < 300) showToast('Filter preferences saved to cloud!', 'success'); };
                x.send(JSON.stringify(filters));
              }} className="btn-ocean" data-testid="save-filters-btn"
                style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(253, 230, 138,0.1))', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="fas fa-save" style={{ marginRight: 6 }} /> Save Filters
              </button>
              <button onClick={() => {
                const x = new XMLHttpRequest();
                x.open('GET', `${API}/filter-preferences`, true);
                x.setRequestHeader('Authorization', `Bearer ${token}`);
                x.onload = () => {
                  if (x.status < 300) { try { loadPreferences(JSON.parse(x.responseText)); showToast('Filter preferences loaded!', 'success'); } catch(e) {} }
                };
                x.send();
              }} className="btn-ocean" data-testid="load-filters-btn"
                style={{ background: 'linear-gradient(135deg, rgba(253, 230, 138,0.1), rgba(252, 211, 77,0.08))', border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                <i className="fas fa-cloud-download-alt" style={{ marginRight: 6 }} /> Load Filters
              </button>
            </div>
            <div style={{ borderTop: '1px solid rgba(253, 230, 138,0.1)', paddingTop: 14 }}>
              <h4 style={{ color: '#047857', fontSize: '0.9rem', margin: '0 0 10px' }}><i className="fas fa-share-alt" style={{ marginRight: 6 }} /> Share Your Filters</h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <button onClick={() => {
                  const filters = exportFilters();
                  const name = prompt('Name this filter preset:') || 'My Filter Preset';
                  const x = new XMLHttpRequest();
                  x.open('POST', `${API}/filter-share`, true);
                  x.setRequestHeader('Content-Type', 'application/json');
                  x.setRequestHeader('Authorization', `Bearer ${token}`);
                  x.onload = () => { if (x.status < 300) { try { const res = JSON.parse(x.responseText); setShareCode(res.share_code); showToast(`Filter shared! Code: ${res.share_code}`, 'success'); } catch(e) {} } };
                  x.send(JSON.stringify({ name, filters, description: '' }));
                }} className="btn-ocean" data-testid="share-filters-btn"
                  style={{ background: 'linear-gradient(135deg, rgba(252, 211, 77,0.12), rgba(253, 230, 138,0.08))', border: '1px solid rgba(252, 211, 77,0.3)', color: '#047857', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  <i className="fas fa-link" style={{ marginRight: 6 }} /> Create Share Link
                </button>
              </div>
              {shareCode && (
                <div style={{ background: 'rgba(252, 211, 77,0.08)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid rgba(252, 211, 77,0.2)' }}>
                  <span style={{ color: '#c4b5fd', fontSize: '0.78rem', fontFamily: 'monospace' }}>{shareCode}</span>
                  <button onClick={() => { navigator.clipboard.writeText(shareCode); showToast('Share code copied!', 'success'); }}
                    style={{ background: 'rgba(252, 211, 77,0.15)', border: '1px solid rgba(252, 211, 77,0.3)', color: '#047857', padding: '3px 8px', borderRadius: 4, fontSize: '0.68rem', cursor: 'pointer' }}>Copy</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" value={importCode} onChange={e => setImportCode(e.target.value)}
                  placeholder="Enter share code to import..." data-testid="import-filter-input"
                  style={{ flex: 1, background: 'rgba(253, 230, 138,0.06)', border: '1px solid rgba(253, 230, 138,0.2)', color: '#334155', padding: '6px 10px', borderRadius: 6, fontSize: '0.8rem' }} />
                <button onClick={() => {
                  if (!importCode.trim()) return;
                  const x = new XMLHttpRequest();
                  x.open('POST', `${API}/filter-share/${importCode.trim()}/import`, true);
                  x.setRequestHeader('Authorization', `Bearer ${token}`);
                  x.onload = () => {
                    if (x.status < 300) { try { const res = JSON.parse(x.responseText); loadPreferences(res.filters); showToast(`Imported "${res.name}" from ${res.from_user}`, 'success'); setImportCode(''); } catch(e) {} }
                    else showToast('Share code not found', 'error');
                  };
                  x.send();
                }} className="btn-ocean" data-testid="import-filter-btn"
                  style={{ background: 'rgba(253, 230, 138,0.12)', border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20 }}><PushNotifications showToast={showToast} /></div>
        <div style={{ marginTop: 20 }}><NotificationPreferences token={token} showToast={showToast} /></div>
        <div style={{ marginTop: 20 }}><WeeklyDigestPreferences token={token} showToast={showToast} /></div>
        <div style={{ marginTop: 20 }}><DataExport showToast={showToast} /></div>

        {/* Web Content Dump */}
        <div style={{ marginTop: 20 }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ color: '#334155', margin: '0 0 12px', fontSize: '1rem' }}>
              <i className="fas fa-file-alt" style={{ marginRight: 8, color: '#047857' }} />
              Web Content Dump
            </h3>
            <p style={{ color: '#a1a1aa', fontSize: '0.85rem', marginBottom: 14 }}>
              Extract and save the full text content from any URL for analysis.
            </p>
            <button
              data-testid="settings-dump-content-btn"
              onClick={() => setShowContentDump(true)}
              style={{
                background: 'linear-gradient(135deg, #FCD34D, #FDE68A)',
                border: 'none', color: '#0F172A', padding: '10px 20px',
                borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600
              }}
            >
              <i className="fas fa-download" style={{ marginRight: 6 }} />
              Dump URL Content
            </button>
          </div>
        </div>

        {/* Admin-Only: Protocol Prompt Timing Config */}
        {user?.is_admin && <ProtocolPromptConfig token={token} showToast={showToast} />}
      </div>

      {/* Content Dump Modal */}
      <ContentDumpModal isOpen={showContentDump} onClose={() => setShowContentDump(false)} />
    </div>
  );
};

/** Admin panel for configuring protocol prompt timing */
const ProtocolPromptConfig = ({ token, showToast }) => {
  const { get, put } = useXHR(token);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get('/admin/protocol-prompt-config', {
      onSuccess: (data) => setConfig(data)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => {
    if (!config) return;
    setSaving(true);
    put('/admin/protocol-prompt-config', {
      body: config,
      onSuccess: () => showToast('Protocol prompt config saved!', 'success'),
      onError: () => showToast('Failed to save config', 'error'),
      onFinally: () => setSaving(false)
    });
  };

  if (!config) return null;

  return (
    <div style={{ marginTop: 20, padding: 15, background: 'rgba(245,158,11,0.06)', borderRadius: 10, border: '1px solid rgba(245,158,11,0.2)' }} data-testid="protocol-prompt-config">
      <h3 style={{ color: '#f59e0b', marginTop: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="fas fa-cog" /> Protocol Prompt Configuration (Admin)
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ color: '#d4d4d8', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>Delay (minutes)</label>
          <input type="number" min="0" max="1440" value={config.delay_minutes ?? 5}
            onChange={e => setConfig(c => ({ ...c, delay_minutes: parseInt(e.target.value) || 0 }))}
            data-testid="prompt-delay-input"
            style={{ width: '100%', background: 'rgba(253, 251, 247,0.8)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, color: '#334155', padding: '6px 10px', fontSize: '0.85rem', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ color: '#d4d4d8', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>Show after N searches</label>
          <input type="number" min="0" max="100" value={config.show_after_searches ?? 3}
            onChange={e => setConfig(c => ({ ...c, show_after_searches: parseInt(e.target.value) || 0 }))}
            data-testid="prompt-after-searches-input"
            style={{ width: '100%', background: 'rgba(253, 251, 247,0.8)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, color: '#334155', padding: '6px 10px', fontSize: '0.85rem', boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ color: '#d4d4d8', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>Prompt Message</label>
        <input type="text" value={config.prompt_message ?? ''} maxLength={300}
          onChange={e => setConfig(c => ({ ...c, prompt_message: e.target.value }))}
          data-testid="prompt-message-input"
          style={{ width: '100%', background: 'rgba(253, 251, 247,0.8)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, color: '#334155', padding: '6px 10px', fontSize: '0.85rem', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#d4d4d8', fontSize: '0.85rem' }}>
          <input type="checkbox" checked={config.enabled ?? true}
            onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))}
            data-testid="prompt-enabled-toggle" />
          Enabled
        </label>
        <button onClick={save} disabled={saving} data-testid="save-prompt-config-btn"
          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#0F172A', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
          {saving ? 'Saving...' : 'Save Config'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
